import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import type { VeriteCard } from '../data/coquin';

interface CoquinVeriteProps {
  players: string[];
  cards: VeriteCard[];
  onBack: () => void;
}

const SLOT_DURATION = 1200;
const SLOT_TICKS = 16;

const PLACEHOLDER_CARD: VeriteCard = { question: 'Aucune question disponible pour l\'instant 🌶️', niveau: 1 };

export default function CoquinVerite({ players, cards, onBack }: CoquinVeriteProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [currentCard, setCurrentCard] = useState<VeriteCard>(() =>
    cards.length > 0 ? cards[Math.floor(Math.random() * cards.length)] : PLACEHOLDER_CARD
  );
  const [choice, setChoice] = useState<'assume' | 'bois' | null>(null);
  const lastCardRef = useRef('');
  const turnCount = useRef(0);

  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [isSlotSpinning, setIsSlotSpinning] = useState(true);
  const [slotDisplayName, setSlotDisplayName] = useState('');
  const slotTimers = useRef<number[]>([]);

  const shuffledOrder = useMemo(() => {
    const indices = players.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, [players]);

  const runSlotMachine = useCallback((targetName: string) => {
    setIsSlotSpinning(true);
    const interval = SLOT_DURATION / SLOT_TICKS;
    for (let i = 0; i < SLOT_TICKS; i++) {
      const timerId = window.setTimeout(() => {
        if (i < SLOT_TICKS - 1) {
          setSlotDisplayName(players[Math.floor(Math.random() * players.length)]);
        } else {
          setSlotDisplayName(targetName);
          setIsSlotSpinning(false);
          try { Haptics.impact({ style: ImpactStyle.Medium }); } catch {}
        }
      }, interval * i + (i > SLOT_TICKS * 0.7 ? (i - SLOT_TICKS * 0.7) * 30 : 0));
      slotTimers.current.push(timerId);
    }
  }, [players]);

  useEffect(() => {
    const timers = slotTimers.current;
    const currentPlayer = players[shuffledOrder[0 % shuffledOrder.length]] || '';
    runSlotMachine(currentPlayer);
    return () => { timers.forEach(id => window.clearTimeout(id)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFlip = useCallback(() => {
    if (isFlipped || isSlotSpinning) return;
    setIsFlipped(true);
    try { Haptics.impact({ style: ImpactStyle.Medium }); } catch {}
  }, [isFlipped, isSlotSpinning]);

  const handleAssume = () => {
    setChoice('assume');
    try { Haptics.impact({ style: ImpactStyle.Light }); } catch {}
  };

  const handleBois = () => {
    setChoice('bois');
    try { Haptics.impact({ style: ImpactStyle.Heavy }); } catch {}
  };

  const handleNextTurn = () => {
    turnCount.current += 1;
    setIsFlipped(false);
    setChoice(null);
    setTimeout(() => {
      let next = PLACEHOLDER_CARD;
      if (cards.length > 0) {
        const avail = cards.filter(c => c.question !== lastCardRef.current);
        const pool = avail.length > 0 ? avail : cards;
        next = pool[Math.floor(Math.random() * pool.length)];
        lastCardRef.current = next.question;
      }
      setCurrentCard(next);
      const nextIndex = (currentPlayerIndex + 1) % players.length;
      setCurrentPlayerIndex(nextIndex);
      const nextPlayer = players[shuffledOrder[nextIndex % shuffledOrder.length]] || '';
      runSlotMachine(nextPlayer);
    }, 300);
  };

  const fontSize = currentCard.question.length > 100 ? '16px' : currentCard.question.length > 60 ? '18px' : '20px';

  return (
    <div className="h-screen bg-gradient-to-b from-[#450a0a] via-[#5c1010] to-[#2a0505] flex flex-col items-center safe-top safe-bottom">
      <button onClick={onBack} className="absolute left-6 z-20 w-10 h-10 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center text-white/60 btn-press border border-white/10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>←</button>

      {/* Turn counter */}
      <div className="absolute right-6 z-10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>
        <span className="text-white/30 font-body text-sm">Tour {turnCount.current + 1}</span>
      </div>

      <div className="flex-shrink-0 pt-14 pb-2 px-6 text-center">
        <p className="text-red-300/50 font-body text-xs mb-1">C'est au tour de</p>
        <div className="h-12 flex items-center justify-center overflow-hidden">
          <h1 key={slotDisplayName} className={`font-display font-black text-3xl text-white drop-shadow-lg ${isSlotSpinning ? 'slot-tick' : 'slot-land'}`}>{slotDisplayName}</h1>
        </div>
        <p className={`text-pink-300/60 font-body text-sm mt-1 ${!isFlipped ? 'tp-pulse' : ''}`}>
          {!isFlipped ? 'Retourne la carte...' : 'Réponds ou bois !'}
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 w-full">
        <div className="w-full max-w-sm">
          <div
            className={`tp-card-flip-v w-full ${!isFlipped && !isSlotSpinning ? 'cursor-pointer tp-breathe' : ''}`}
            style={{ height: 'min(55vh, 380px)' }}
            onClick={handleFlip}
          >
            <div className={`tp-card-inner ${isFlipped ? 'tp-flipped' : ''}`}>
              <div className="tp-card-face tp-card-front">
                <div className="w-full h-full rounded-3xl bg-gradient-to-br from-red-600 via-red-700 to-red-900 border border-red-400/20 flex items-center justify-center shadow-[0_8px_30px_rgba(239,68,68,0.3)]">
                  <span className="text-6xl opacity-70">🌶️</span>
                </div>
              </div>
              <div className="tp-card-face tp-card-back-reveal">
                <div className="w-full h-full rounded-3xl bg-gradient-to-br from-red-500 via-pink-500 to-red-600 border border-pink-400/30 flex flex-col items-center justify-center px-6 py-6 shadow-[0_8px_30px_rgba(244,114,182,0.3)]">
                  <span className="text-3xl mb-3">🌶️</span>
                  <p className="text-white font-display font-bold text-center leading-relaxed flex-1 flex items-center" style={{ fontSize }}>
                    {currentCard.question}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {isFlipped && !choice && (
            <div className="flex gap-3 mt-5 animate-slide-up">
              <button onClick={handleAssume} className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-green-400 to-emerald-500 text-white font-display font-bold text-base btn-press">J'assume ✅</button>
              <button onClick={handleBois} className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-red-500 to-red-600 text-white font-display font-bold text-base btn-press">Je bois 🍺</button>
            </div>
          )}

          {choice === 'assume' && (
            <div className="mt-5 text-center animate-slide-up">
              <div className="py-4 rounded-2xl bg-green-500/20 border border-green-400/30">
                <span className="text-3xl">✅</span>
                <p className="text-green-400 font-display font-bold text-lg mt-1">Respect !</p>
              </div>
            </div>
          )}

          {choice === 'bois' && (
            <div className="mt-5 text-center animate-slide-up">
              <p className="text-red-400/60 font-body text-sm">🍺 Le groupe décide !</p>
            </div>
          )}
        </div>
      </div>

      {/* Tour suivant — fixed at bottom, shown immediately when choice is made */}
      {choice && (
        <div className="flex-shrink-0 px-6 pb-6 w-full" style={{ maxWidth: '420px', margin: '0 auto' }}>
          <button
            onClick={handleNextTurn}
            className="w-full py-4 rounded-full bg-gradient-to-r from-red-500 to-pink-400 text-white font-display font-bold text-base btn-press shadow-[0_4px_20px_rgba(239,68,68,0.3)]"
          >
            Tour suivant →
          </button>
        </div>
      )}
    </div>
  );
}
